import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import useMediaQuery from "@mui/material/useMediaQuery";
import { GATE_COLOR } from "../../../interfaces/robot/rooms/gate";
import { WAYPOINT_COLOR, WaypointPart } from "../../../interfaces/robot/rooms/waypoints";
import { LANDMARK_COLOR, EVACUATION_PLACE_COLOR } from "../../../interfaces/robot/rooms/landmark";
import ZonePointType, { SimpleZonePointType } from "../../../enums/robot/rooms/zone-point-type";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectSelectedRoom,
  selectSelectedRoomLandmarks,
  selectSelectedRoomLimitZone,
  selectWaypointsData,
} from "../../../store/room/room-selectors";
import {
  selectSelectedZone,
  selectSelectedZoneInstance,
  selectZonePlannerModeState,
  selectZonePlannerMouseState,
  selectCurrentTeachingAutoZonePath,
  selectTeachAndRepeatPathByZoneId,
  selectWaypointsRestSet,
} from "../../../store/room/zone-planner/zone-planner-selectors";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import ZonePlannerModeState from "../../../enums/robot/zone-planner/zone-planner-mode-state";
import { getRosPointFromSvgCoordinates } from "../../../interfaces/robot/rooms/point";
import { ZoneTypography } from "./ZoneTypography";
import { MapHistoryAction, MapHistoryType } from "../../../interfaces/robot/zone-planner/map-history";
import { isCleaningZone } from "../../../interfaces/robot/rooms/cleaning-zone";
import { selectSystemSettings } from "../../../store/robot-configurations/robot-configurations-selectors";

const useStyles = makeStyles()((theme) => ({
  svgCircleCleaningZonePoint: {
    fill: "green",
  },
  svgCircleTeachAndRepeatZonePoint: {
    fill: "#118FF3",
  },
  svgCircleRedZonePoint: {
    fill: "red",
  },
  svgCircleRuleZonePoint: {
    fill: "purple",
  },
  svgCircleGatePoint: {
    fill: GATE_COLOR,
  },
  svgCircleCleaningZoneStartPoint: {
    fill: "rgb(160, 0, 160)",
  },
  svgCircleOriginPoint: {
    fill: "rgb(255, 50, 240)",
  },
  svgCircleCleaningZoneMidPoint: {
    fill: "rgb(40, 200, 0)",
    stroke: "green",
    fillOpacity: 0.6,
  },
  svgCircleRedZoneMidPoint: {
    fill: "rgb(245, 80, 80)",
    stroke: "rgb(245, 80, 80)",
    fillOpacity: 0.5,
  },
  svgCircleRuleZoneMidPoint: {
    fill: "rgb(180, 90, 160)",
    stroke: "rgb(180, 90, 160)",
    fillOpacity: 0.5,
  },
  svgCircleWayPoint: {
    fill: WAYPOINT_COLOR,
    filter: "drop-shadow(0 0 0 #000000)",
  },
  svgCircleLandmarkPoint: {
    fill: LANDMARK_COLOR,
  },
  svgCircleEvacuationPlacePoint: {
    fill: EVACUATION_PLACE_COLOR,
  },
  svgCircleRoomLimitZonePoint: {
    fill: "black",
  },
  svgCircleRoomLimitZoneMidPoint: {
    fill: "rgb(211,211,211)",
    stroke: "black",
    fillOpacity: 0.5,
  },
  svgCircleTeachAndRepeatPathPoint: {
    fill: "rgb(200,200,230)",
    stroke: "rgb(0,0,150)",
    fillOpacity: 1,
  },
  svgCircleTeachAndRepeatPathMidPoint: {
    fill: "rgb(200,200,230)",
    stroke: "rgb(200,200,230)",
    fillOpacity: 0.5,
  },
  svgCircleTeachAndRepeatZoneMidPoint: {
    fill: "rgb(17, 143, 243)",
    stroke: "#118FF3",
    fillOpacity: 0.5,
  },
  svgHover: {
    opacity: 0,
    "@media (hover: hover)": {
      "&:hover": {
        opacity: 0.5,
      },
    },
  },
  svgPointSelected: {
    opacity: 0.5,
  },
  notSelectable: {
    pointerEvents: "none",
    opacity: 0.4,
  },
}));

const ZoneSvgPoint: React.FC<{
  point: { x: number; y: number };
  id: string | null;
  scale: number;
  type: ZonePointType;
  enlargingOnHoverEnabled: boolean;
  midPointInsertAtIndex: number | null;
  preventPointerEvents?: boolean;
  deletable?: boolean;
  draggable?: boolean;
  isRoomLimitZonePoint?: boolean;
  oversize?: boolean;
  onClickOverride?: (id: string) => void;
  oversizeSelectedColor?: string;
  notSelectable?: boolean;
}> = ({
  point,
  id,
  scale,
  type,
  enlargingOnHoverEnabled,
  midPointInsertAtIndex,
  preventPointerEvents,
  deletable = true,
  draggable = true,
  isRoomLimitZonePoint = false,
  oversize,
  onClickOverride,
  oversizeSelectedColor,
  notSelectable = false
}) => {
  const { classes, cx } = useStyles();

  const dispatch = useSharedDispatch();

  const {
    setSelectedZoneSelectedPoint,
    setPreviousRoomLimitZone,
    setRoomLimitZoneSelectedPointId,
    setSelectedWayPoint,
    setSelectedLandmark,
    setSelectedGatePoint,
    addSelectedZonePoint,
    addRoomLimitZonePoint,
    addTeachAndRepeatPathPoint,
    setSelectedTeachAndRepeatZonePathPoint,
    patchCleaningZone,
    patchRedZone,
    patchRuleZone,
    deleteLandmarkById,
    upsertWaypointSet,
    patchLimitZone,
    addUndoableAction,
    setZonePlannerMouseState,
  } = useSharedActions();

  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const zonePlannerMode = useSharedSelector((state) => selectZonePlannerModeState(state));
  const roomData = useSharedSelector((state) => selectSelectedRoom(state));
  const selectedZoneInstance = useSharedSelector((state) => selectSelectedZoneInstance(state));
  const waypointsData = useSharedSelector((state) => selectWaypointsData(state));
  const selectedZone = useSharedSelector((state) => selectSelectedZone(state));
  const selectedTrPath = useSharedSelector((state) => selectCurrentTeachingAutoZonePath(state));
  const selectedTrPathInstance = useSharedSelector((state) => selectTeachAndRepeatPathByZoneId(state, selectedZone.id));
  const landmarks = useSharedSelector((state) => selectSelectedRoomLandmarks(state));
  const limitZone = useSharedSelector((state) => selectSelectedRoomLimitZone(state));

  const { t } = useTranslation();

  const RADIUS = oversize ? 10 : 5;

  const matchesMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  useEffect(() => {
    const mouseUpHandler = (_event: MouseEvent | TouchEvent) => {
      dispatch(setSelectedZoneSelectedPoint({ pointId: null }));
      dispatch(setRoomLimitZoneSelectedPointId(null));

      if (zonePlannerMouseState === ZoneControllerMouseState.DragZone) {
        dispatch(setSelectedWayPoint({ pointId: null }));
        dispatch(setSelectedLandmark(null));
        dispatch(setSelectedGatePoint({ pointId: null }));
      }
    };

    window.addEventListener("mouseup", mouseUpHandler);
    window.addEventListener("touchend", mouseUpHandler);

    return () => {
      window.removeEventListener("mouseup", mouseUpHandler);
      window.removeEventListener("touchend", mouseUpHandler);
    };
  }, [
    dispatch,
    type,
    zonePlannerMouseState,
    setRoomLimitZoneSelectedPointId,
    setSelectedGatePoint,
    setSelectedLandmark,
    setSelectedWayPoint,
    setSelectedZoneSelectedPoint,
  ]);

  const isEnlargingOnHoverEnabled =
    enlargingOnHoverEnabled &&
    (zonePlannerMode === ZonePlannerModeState.EditAutoGeneratedTeachAndRepeatPath ||
      type !== ZonePointType.TeachAndRepeatZoneStartPosition) &&
    !(type === ZonePointType.GatePoint && zonePlannerMouseState !== ZoneControllerMouseState.DragZone);

  const clickHandler = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.detail === 2){ // this is the second click of a double click and it accidentally hit a point because there was a point where we clicked
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
      return;
    }
    
    if (preventPointerEvents) return;

    if (onClickOverride != null && id != null) {
      onClickOverride(id);
      return;
    }

    if (
      ((zonePlannerMouseState === ZoneControllerMouseState.DeletePoint && selectedZoneInstance !== null) ||
        (zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint && isRoomLimitZonePoint)) &&
      id != null &&
      type !== ZonePointType.CleaningZoneStartPosition &&
      type !== ZonePointType.TeachAndRepeatZoneStartPosition &&
      deletable
    ) {
      if (type === ZonePointType.RoomLimitZonePoint && limitZone != null) {
        const oldLimitZone = {
          ...limitZone,
          points: limitZone.points.map(p => ({...p})),
        }
        dispatch(setPreviousRoomLimitZone(oldLimitZone));
        dispatch(
          patchLimitZone({
            id: limitZone.id,
            data: { points: limitZone.points.filter((p) => p.point.id !== id)},
          })
        );
      } else if (selectedZoneInstance != null) {
        if (
          type === ZonePointType.TeachAndRepeatZonePathPoint &&
          isCleaningZone(selectedZoneInstance) &&
          selectedTrPathInstance != null &&
          selectedTrPathInstance.path != null &&
          selectedTrPathInstance.path.length > 2
        ) 
        {
          dispatch(
            addUndoableAction({
              action: MapHistoryAction.Patched,
              type: MapHistoryType.TeachAndRepeatZonePath,
              changedData: {
                id: selectedTrPathInstance.id,
                zoneId: selectedZoneInstance.id,
                path: selectedTrPathInstance.path.map(pt => ({...pt})),
              }
            })
          );
          let startPosition = undefined
          if( id === selectedTrPathInstance.path[0].id){
            startPosition = {
              coordinateX : selectedTrPathInstance.path[1].coordinateX,
              coordinateY: selectedTrPathInstance.path[1].coordinateY,
              coordinateTheta: selectedTrPathInstance.path[1].coordinateTheta,
              id: selectedTrPathInstance.path[1].id,
              type: ZonePointType.CleaningZoneStartPosition
              };
          }
          // when we remove the first point of the path, we also need to update the start point of the corresponding cleaning zone
            dispatch( patchCleaningZone({
              id: selectedTrPathInstance.zoneId,
              data: {
                ...(startPosition !== null ? {cleaningZone: {startPosition: startPosition}}: null),
                teachAndRepeatPath: { path: selectedTrPathInstance.path.filter((p) => p.id !== id).map(pt => ({...pt})) },
              }
            }));
        } else if (type === ZonePointType.CleaningZoneCorner || type === ZonePointType.TeachAndRepeatZoneCorner) {
          dispatch(
            addUndoableAction({
              action: MapHistoryAction.Patched,
              type: MapHistoryType.CleaningZone,
              changedData: {
                id: selectedZoneInstance.id,
                points: [...selectedZoneInstance.points],
              },
            })
          );
          dispatch(
            patchCleaningZone({
              id: selectedZoneInstance.id,
              data: {cleaningZone: { points: selectedZoneInstance.points.filter((p) => p.point.id !== id) } },
            })
          );
        } else if (type === ZonePointType.RedZoneCorner) {
          dispatch(
            addUndoableAction({
              action: MapHistoryAction.Patched,
              type: MapHistoryType.RedZone,
              changedData: {
                id: selectedZoneInstance.id,
                points: [...selectedZoneInstance.points],
              },
            })
          );
          dispatch(
            patchRedZone({
              id: selectedZoneInstance.id,
              data: { points: selectedZoneInstance.points.filter((p) => p.point.id !== id) },
            })
          );
        } else if (type === ZonePointType.RuleZoneCorner) {
          dispatch(
            addUndoableAction({
              action: MapHistoryAction.Patched,
              type: MapHistoryType.RuleZone,
              changedData: {
                id: selectedZoneInstance.id,
                points: [...selectedZoneInstance.points],
              },
            })
          );
          dispatch(
            patchRuleZone({
              id: selectedZoneInstance.id,
              data: { points: selectedZoneInstance.points.filter((p) => p.point.id !== id) },
            })
          );
        }
      }
    } else if (
      zonePlannerMouseState === ZoneControllerMouseState.DeleteZone &&
      type === ZonePointType.WayPoint &&
      id !== null && roomData &&
      deletable
    ) {
      dispatch(
        addUndoableAction({
          action: MapHistoryAction.Patched,
          type: MapHistoryType.WaypointSet,
          changedData: {
            nodes: [...waypointsData.data.nodes.filter(n => n.room === roomData.id)],
            edges: [...waypointsData.data.edges.filter(e => e.room === roomData.id)],
          },
        })
      );

      const nodesInRoom = waypointsData.data.nodes.filter(n => n.room === roomData.id && n.id !== id);
      const edgesInRoom = waypointsData.data.edges.filter(e => e.room === roomData.id);

      const remainingEdges = edgesInRoom.filter(e => e.u !== id && e.v !== id);
      const connectedNodeIds = new Set<string>();
      for (const edge of remainingEdges) {
        connectedNodeIds.add(edge.u);
        connectedNodeIds.add(edge.v);
      }
      const remainingNodes = nodesInRoom.filter(n => connectedNodeIds.has(n.id) && n.id !== id);

      dispatch(upsertWaypointSet({ room: roomData.id, nodes: remainingNodes, edges: remainingEdges }));
    } else if (
      zonePlannerMouseState === ZoneControllerMouseState.DeleteZone &&
      type === ZonePointType.LandmarkPoint &&
      id !== null
    ) {
      const existingLandmark = landmarks.find((l) => l.id === id);
      if (existingLandmark != null) {
        dispatch(
          addUndoableAction({
            action: MapHistoryAction.Deleted,
            type: MapHistoryType.Landmark,
            changedData: { ...existingLandmark },
          })
        );
      }
      dispatch(deleteLandmarkById(id));
    } else {
      dispatch(setSelectedZoneSelectedPoint({ pointId: null }));
      dispatch(setRoomLimitZoneSelectedPointId(null));
    }
  };

  const handleMouseDownTouchStart = (event: React.MouseEvent | React.TouchEvent) => {
    event.stopPropagation();
    if ("ontouchstart" in window && event.type === "mousedown") {
      return;
    }
    if (event.detail === 2){ // this is the second click of a double click and it accidentally hit a point because there was a point where we clicked
      dispatch(setZonePlannerMouseState(ZoneControllerMouseState.ShowDetails));
      return;
    }
    if (preventPointerEvents) return;

    if (
      roomData != null &&
      id == null &&
      midPointInsertAtIndex != null &&
      (selectedZoneInstance != null || isRoomLimitZonePoint)
    ) {
      const newPoint = getRosPointFromSvgCoordinates(point.x, point.y, roomData);
      if (newPoint !== null) {
        if (isRoomLimitZonePoint) {
          dispatch(addRoomLimitZonePoint({ point: newPoint, insertAtIndex: midPointInsertAtIndex }));
        } else if (type === ZonePointType.TeachAndRepeatZonePathMidPoint) {
          dispatch(addTeachAndRepeatPathPoint({ point: newPoint, insertAtIndex: midPointInsertAtIndex }));
        } else {
          dispatch(
            addSelectedZonePoint({
              point: newPoint,
              type: SimpleZonePointType.CornerPoint,
              insertAtIndex: midPointInsertAtIndex,
              id: uuidv4(),
              individualPointCreated: true,
            })
          );
        }
      }
    } else if (
      id !== null &&
      type === ZonePointType.WayPoint &&
      zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint
    ) {
      dispatch(setSelectedWayPoint({ pointId: id }));
    } else if (
      id !== null &&
      type === ZonePointType.LandmarkPoint &&
      (zonePlannerMouseState === ZoneControllerMouseState.DragZone ||
        zonePlannerMouseState === ZoneControllerMouseState.Default)
    ) {
      dispatch(setSelectedLandmark(id));
    } else if (
      id !== null &&
      type === ZonePointType.WayPoint &&
      zonePlannerMouseState === ZoneControllerMouseState.DragZone
    ) {
      dispatch(setSelectedWayPoint({ pointId: id, isMoving: true }));
    } else if (
      id !== null &&
      type === ZonePointType.GatePoint &&
      zonePlannerMouseState === ZoneControllerMouseState.DragZone &&
      draggable
    ) {
      dispatch(setSelectedGatePoint({ pointId: id, updateHistory: true }));
    } else if (
      type === ZonePointType.TeachAndRepeatZonePathPoint &&
      draggable &&
      zonePlannerMouseState !== ZoneControllerMouseState.DeletePoint &&
      zonePlannerMouseState !== ZoneControllerMouseState.DeleteRoomLimitationZonePoint
    ) {
      dispatch(setSelectedTeachAndRepeatZonePathPoint(id));
    } else if (
      zonePlannerMouseState !== ZoneControllerMouseState.DeletePoint &&
      zonePlannerMouseState !== ZoneControllerMouseState.DeleteRoomLimitationZonePoint
    ) {
      dispatch(setSelectedZoneSelectedPoint({ pointId: id, isMoving: true }));
      dispatch(setRoomLimitZoneSelectedPointId(id));
    }
  };

  let circleClasses;
  switch (type) {
    case ZonePointType.CleaningZoneCorner:
      circleClasses = classes.svgCircleCleaningZonePoint;
      break;
    case ZonePointType.CleaningZoneStartPosition:
    case ZonePointType.TeachAndRepeatZoneStartPosition:
      circleClasses = classes.svgCircleCleaningZoneStartPoint;
      break;
    case ZonePointType.TeachAndRepeatZoneCorner:
      circleClasses = classes.svgCircleTeachAndRepeatZonePoint;
      break;
    case ZonePointType.RedZoneCorner:
      circleClasses = classes.svgCircleRedZonePoint;
      break;
    case ZonePointType.RuleZoneCorner:
      circleClasses = classes.svgCircleRuleZonePoint;
      break;
    case ZonePointType.MapOrigin:
      circleClasses = classes.svgCircleOriginPoint;
      break;
    case ZonePointType.CleaningZoneMidPoint:
      circleClasses = classes.svgCircleCleaningZoneMidPoint;
      break;
    case ZonePointType.RedZoneMidPoint:
      circleClasses = classes.svgCircleRedZoneMidPoint;
      break;
    case ZonePointType.RuleZoneMidPoint:
      circleClasses = classes.svgCircleRuleZoneMidPoint;
      break;
    case ZonePointType.WayPoint:
      circleClasses = cx(classes.svgCircleWayPoint, {[classes.notSelectable]: notSelectable});
      break;
    case ZonePointType.LandmarkPoint:
      circleClasses = zonePlannerMode === ZonePlannerModeState.EvacuationMode ? classes.svgCircleEvacuationPlacePoint : classes.svgCircleLandmarkPoint;
      break;
    case ZonePointType.GatePoint:
      circleClasses = cx(classes.svgCircleGatePoint, {[classes.notSelectable]: notSelectable});
      break;
    case ZonePointType.RoomLimitZonePoint:
      circleClasses = classes.svgCircleRoomLimitZonePoint;
      break;
    case ZonePointType.RoomLimitZonePointMidPoint:
      circleClasses = classes.svgCircleRoomLimitZoneMidPoint;
      break;
    case ZonePointType.TeachAndRepeatZonePathPoint:
      circleClasses = classes.svgCircleTeachAndRepeatPathPoint;
      break;
    case ZonePointType.TeachAndRepeatZonePathMidPoint:
      circleClasses = classes.svgCircleTeachAndRepeatPathMidPoint;
      break;
    case ZonePointType.TeachAndRepeatZoneMidPoint:
      circleClasses = classes.svgCircleTeachAndRepeatZoneMidPoint;
      break;
    default:
      break;
  }

  let pointTooltip;
  if (
    [
      ZonePointType.RedZoneMidPoint,
      ZonePointType.RuleZoneMidPoint,
      ZonePointType.CleaningZoneMidPoint,
      ZonePointType.TeachAndRepeatZoneMidPoint,
      ZonePointType.TeachAndRepeatZonePathMidPoint,
    ].some((t) => t === type)
  ) {
    pointTooltip = t("tooltip-help-add-new-point") as string;
  } else if (
    zonePlannerMouseState === ZoneControllerMouseState.DeletePoint ||
    zonePlannerMouseState === ZoneControllerMouseState.DeleteRoomLimitationZonePoint ||
    (type === ZonePointType.WayPoint && zonePlannerMouseState === ZoneControllerMouseState.DeleteZone) ||
    (type === ZonePointType.LandmarkPoint && zonePlannerMouseState === ZoneControllerMouseState.DeleteZone)
  ) {
    if (type === ZonePointType.CleaningZoneStartPosition || !deletable) {
      pointTooltip = t("tooltip-cant-delete-point") as string;
    } else {
      pointTooltip = t("tooltip-help-delete-point") as string;
    }
  } else if (type === ZonePointType.WayPoint && zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint) {
    if (
      waypointsData.selectedNodeId !== null &&
      waypointsData.mousePosition !== null &&
      waypointsData.selectedNodeId !== id
    ) {
      pointTooltip = t("tooltip-help-connect-this-waypoint") as string;
    } else if (waypointsData.selectedNodeId !== null && waypointsData.mousePosition !== null) {
      pointTooltip = t("tooltip-help-cant-connect-self-waypoints") as string;
    } else {
      pointTooltip = t("tooltip-help-connect-waypoints") as string;
    }
  } else if (type === ZonePointType.LandmarkPoint && zonePlannerMouseState === ZoneControllerMouseState.Default) {
    pointTooltip = t("tooltip-help-select-point") as string;
  } else if (draggable) {
    pointTooltip = t("tooltip-help-move-point") as string;
  }

  let strokeWidth = 1;
  if (
    type === ZonePointType.CleaningZoneMidPoint ||
    type === ZonePointType.RedZoneMidPoint ||
    type === ZonePointType.RuleZoneMidPoint ||
    type === ZonePointType.RoomLimitZonePointMidPoint ||
    type === ZonePointType.TeachAndRepeatZonePathPoint ||
    type === ZonePointType.TeachAndRepeatZonePathMidPoint ||
    type === ZonePointType.TeachAndRepeatZoneMidPoint
  ) {
    strokeWidth = 1 / scale;
  }

  const isEnlargedMobile =
    isEnlargingOnHoverEnabled &&
    matchesMdDown &&
    (type === ZonePointType.CleaningZoneCorner ||
      type === ZonePointType.RedZoneCorner ||
      type === ZonePointType.RuleZoneCorner ||
      type === ZonePointType.TeachAndRepeatZoneCorner);

  const isTooltipVisible =
    !preventPointerEvents &&
    !isEnlargedMobile &&
    !matchesMdDown &&
    (type === ZonePointType.RedZoneMidPoint ||
      type === ZonePointType.RuleZoneMidPoint ||
      type === ZonePointType.CleaningZoneMidPoint ||
      type === ZonePointType.TeachAndRepeatZoneMidPoint ||
      type === ZonePointType.TeachAndRepeatZonePathMidPoint ||
      (id !== selectedZone.selectedPointId && selectedTrPath != null && id !== selectedTrPath.selectedTeachAndRepeatPathPointId)) &&
    !(type === ZonePointType.GatePoint && zonePlannerMouseState !== ZoneControllerMouseState.DragZone);

  const outerCircle = (
    <circle
      cx={point.x}
      cy={point.y}
      r={(zonePlannerMouseState === ZoneControllerMouseState.AddWayPoint ? RADIUS * 4 : RADIUS * 3) / scale}
      strokeWidth={strokeWidth}
      onClick={clickHandler}
      onMouseDown={handleMouseDownTouchStart}
      onTouchStart={handleMouseDownTouchStart}
      className={cx(circleClasses, {
        [classes.svgHover]: isEnlargingOnHoverEnabled || isEnlargedMobile,
        [classes.svgPointSelected]: id === waypointsData.selectedNodeId && id !== null,
      })}
      style={{ fill: oversizeSelectedColor != null ? oversizeSelectedColor : undefined }}
      data-testid="outerCircle"
    />
  );

  return (
    <React.Fragment>
      {/* inner circle */}
      <circle
        cx={point.x}
        cy={point.y}
        r={
          (type === ZonePointType.TeachAndRepeatZonePathPoint
            ? 4
            : type === ZonePointType.TeachAndRepeatZonePathMidPoint
            ? 3
            : RADIUS) / scale
        }
        className={circleClasses}
        strokeWidth={strokeWidth}
        style={{
          pointerEvents: preventPointerEvents ? "none" : undefined,
          fill: oversizeSelectedColor != null ? oversizeSelectedColor : undefined,
        }}
      />

      {/* outer "hover" circle. Set opacity = 0 instead of totally hiding, so that onMouseEnter works */}
      {isEnlargingOnHoverEnabled && (
        <Tooltip
          data-testid="outerCircleTooltip"
          title={
            isTooltipVisible ? (
              <ZoneTypography variant="body2" noWrap>
                {pointTooltip}
              </ZoneTypography>
            ) : undefined
          }
        >
          {outerCircle}
        </Tooltip>
      )}
    </React.Fragment>
  );
};

export default ZoneSvgPoint;
